import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PopoverBody, ModalBody } from 'reactstrap';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ReactComponent as SearchIcon } from 'assets/icon-search.svg';
import { ReactComponent as SortByIcon } from 'assets/icon-sort-by.svg';
import { ReactComponent as RefreshIcon } from 'assets/icon-rotate.svg';
import { ReactComponent as AddIcon } from 'assets/icon-add-round.svg';
import { ReactComponent as CheckRoundIcon } from 'assets/icon-check-round.svg';
import { ReactComponent as FilterIcon } from 'assets/icon-filter.svg';
import { ReactComponent as ContactsIcon } from 'assets/icon-contacts.svg';
import { ContactsModal, PopoverSort } from 'shared/components/SharedStyles/SharedStyles';
import { ModalHeader } from 'shared/components/modal/ModalHeaders';
import { TextField } from 'shared/components/form/Inputs';
import { ButtonLabelIcon } from 'shared/components/Buttons';
import { LabelIcon } from 'shared/components/Labels';

import { Checkbox } from 'shared/components/form/CheckBox';
import { colors } from 'shared/defaultTheme';
import FieldType from 'shared/helpers/fieldType';
import { emptyString } from 'shared/utils';

import useApi from 'hooks/useApi';
import {
  changeGeneralSearchTextClient,
  changeSortConfigClient,
  setClearClientList,
  setSelectedCustomField,
} from 'redux/actions/clientActions';
import ContactsImportModal from './ContactsImportModal';

import { OptionMenu, FilterOptionMenu } from '../styles';

const HeaderActions = () => {
  const dispatch = useDispatch();
  const { customAreaApi } = useApi();
  const clientState = useSelector((state) => state.clientState);
  const history = useHistory();
  const { t } = useTranslation();
  const { cusNavId } = useSelector((state) => state.auth);
  const { selectedDefaultLanguage } = useSelector((state) => state.topBar);

  const [orderName, setOrderName] = useState('ClientName');
  const [isOrder, setIsOrder] = useState(true);
  const [customFields, setCustomFields] = useState([]);
  const [openContactsImportModal, setOpenContactsImportModal] = useState(false);

  const { generalSearchText, sortConfig, selectedCustomField } = clientState;

  const [, getFields] = customAreaApi(
    {
      url: `${window.API_V1}/cus/fields/${cusNavId}/search`,
      method: 'POST',
    },
    { manual: true }
  );

  const getDataFields = async () => {
    const {
      data: { data },
    } = await getFields({
      data: {
        filters: [],
        reduce: {
          limit: 0,
          skip: 0,
        },
      },
    });

    const customFieldsWithoutGroup = data.filter(
      (currentCustomField) => emptyString(currentCustomField.parentId) && currentCustomField.id
    );

    const filteredCustomFields = customFieldsWithoutGroup.filter((x) =>
      [
        FieldType.TEXT,
        FieldType.TEXT_AREA,
        FieldType.NUMBER,
        FieldType.CURRENCY,
        FieldType.LIST,
        FieldType.MULTI_LIST,
        FieldType.OPTION,
        FieldType.CALENDAR,
      ].includes(x.type)
    );

    const sortedCustomFields = filteredCustomFields.sort((a, b) => a.order - b.order);

    const modifiedCustomFields = sortedCustomFields.reduce((acc, customField) => {
      if (customField.type === FieldType.OPTION) {
        const duplicatedCustomField = { ...customField };
        duplicatedCustomField.id = `${customField.id}-copy`;
        acc.push(customField, duplicatedCustomField);
      } else {
        acc.push(customField);
      }
      return acc;
    }, []);

    setCustomFields(modifiedCustomFields);
  };

  useEffect(() => {
    getDataFields();
  }, []);

  const getSortIcon = (fieldName) => {
    const sortOption = sortConfig.find(
      (currentSortOption) => currentSortOption.field === fieldName
    );
    if (sortOption) {
      return <CheckRoundIcon fill={colors.black} />;
    }
    return <CheckRoundIcon fill={colors.white} />;
  };

  const getOrderIcon = (isSort) => {
    const sortOption = sortConfig.find((currentSortOption) => currentSortOption.asc === isSort);
    if (sortOption) {
      return <CheckRoundIcon fill={colors.black} />;
    }
    return <CheckRoundIcon fill={colors.white} />;
  };

  const onSort = (fieldName, isSort) => {
    dispatch(changeSortConfigClient({ fieldName, isSort }));
  };

  useEffect(() => {
    onSort(orderName, isOrder);
  }, [orderName, isOrder]);

  const onChangeSearch = (e) => {
    if (e) {
      e.preventDefault();
      const { value } = e.target;
      dispatch(changeGeneralSearchTextClient(value || ''));
    } else {
      dispatch(changeGeneralSearchTextClient(''));
    }
  };

  const onRefresh = () => {
    dispatch(setClearClientList(true));
    onSort('ClientName', true);
  };

  const onClickNewClient = () => history.push('/clients/new');

  const removeCustomField = (index) => {
    const newCustomField = selectedCustomField.filter((item) => item.id !== index);
    dispatch(setSelectedCustomField(newCustomField));
  };

  const onSelectedCustomField = useCallback((e, customField) => {
    const findCustomField = selectedCustomField.find((x) => x.id === customField.id);
    if (!findCustomField) {
      const newCustomField = [...selectedCustomField, { ...customField }];
      dispatch(setSelectedCustomField(newCustomField));
    } else {
      removeCustomField(findCustomField.id);
    }
  });

  const getNameSelectedLanguage = (i18List, type, id) => {
    if (i18List) {
      const findName = i18List.find((x) => x.i18Code === selectedDefaultLanguage);
      if (findName && findName?.i18Name !== '') {
        if (type === FieldType.OPTION && !id.includes('copy')) {
          return `${findName?.i18Name} ${t('general_button_yes')}`;
        }
        if (type === FieldType.OPTION && id.includes('copy')) {
          return `${findName?.i18Name} ${t('general_button_no')}`;
        }
        return findName?.i18Name;
      }
      return i18List[0].i18Name;
    }
    return null;
  };

  const onCloseContactsImportModal = () => {
    setOpenContactsImportModal(false);
  };

  const onOpenContactsImportModal = () => {
    setOpenContactsImportModal(true);
  };

  return (
    <>
      <div className="d-flex justify-content-end action-header-mobile">
        <div className="form w-25 h-50 mr-4 w-container-search-mobile">
          <TextField
            borderRadius={2}
            className="mb-4 w-search-clients m-0 h-50"
            inputClassName="w-100"
            rightIcon={<SearchIcon fill={colors.black} />}
            value={generalSearchText}
            onChange={onChangeSearch}
          />
        </div>
        <div className="d-flex btn-action-clients">
          <ButtonLabelIcon
            id="PopoverFilterClient"
            text={t('general_button_filter')}
            type="button"
            Icon={() => <FilterIcon fill={colors.black} />}
            className="button-icon mr-1"
          />
          <ButtonLabelIcon
            id="PopoverSortClient"
            text={t('general_order_by')}
            type="button"
            Icon={() => <SortByIcon fill={colors.black} />}
            className="button-icon mr-1"
          />
          <ButtonLabelIcon
            id="refresh-icon"
            text={t('general_button_refresh')}
            type="button"
            Icon={() => <RefreshIcon fill={colors.black} />}
            className="button-icon mr-1"
            onClick={onRefresh}
          />
          <ButtonLabelIcon
            id="add-icon"
            text={t('general_button_new')}
            type="button"
            Icon={() => <AddIcon fill={colors.black} />}
            className="button-icon mr-1"
            onClick={onClickNewClient}
          />
          <ButtonLabelIcon
            id="import-clients"
            text={t('general_button_import_contacts')}
            type="button"
            Icon={() => <ContactsIcon fill={colors.black} />}
            className="button-icon"
            onClick={onOpenContactsImportModal}
          />
        </div>
      </div>
      <PopoverSort trigger="legacy" placement="bottom" target="PopoverSortClient">
        <PopoverBody>
          <OptionMenu onClick={() => setOrderName('ClientName')} aria-hidden="true">
            <LabelIcon
              label={t('clients_name_column')}
              color=""
              className="ml-1 body-text--primary action-option-order-label"
              classNameMain="mb-3 label-icon-sort"
              isStyle={false}
              Icon={() => getSortIcon('ClientName')}
            />
          </OptionMenu>
          <OptionMenu onClick={() => setOrderName('Cif')} aria-hidden="true">
            <LabelIcon
              label={t('clients_number_id_column')}
              color=""
              className="ml-1 body-text--primary action-option-order-label"
              classNameMain="mb-3 label-icon-sort"
              isStyle={false}
              Icon={() => getSortIcon('Cif')}
            />
          </OptionMenu>
          <OptionMenu onClick={() => setOrderName('ContactPhone')} aria-hidden="true">
            <LabelIcon
              label={t('clients_mobile_column')}
              color=""
              className="ml-1 body-text--primary action-option-order-label"
              classNameMain="mb-3 label-icon-sort"
              isStyle={false}
              Icon={() => getSortIcon('ContactPhone')}
            />
          </OptionMenu>
          <OptionMenu onClick={() => setOrderName('Direction')} aria-hidden="true">
            <LabelIcon
              label={t('clients_address_column')}
              color=""
              className="ml-1 body-text--primary action-option-order-label"
              classNameMain="mb-3 label-icon-sort"
              isStyle={false}
              Icon={() => getSortIcon('Direction')}
            />
          </OptionMenu>
          <OptionMenu onClick={() => setOrderName('Local')} aria-hidden="true">
            <LabelIcon
              label={t('clients_city_column')}
              color=""
              className="ml-1 body-text--primary action-option-order-label"
              classNameMain="mb-3 label-icon-sort"
              isStyle={false}
              Icon={() => getSortIcon('Local')}
            />
          </OptionMenu>
          <hr />
          <OptionMenu onClick={() => setIsOrder(true)}>
            <LabelIcon
              label={t('general_ascending')}
              color=""
              className="ml-1 body-text--primary action-option-order-label"
              classNameMain="mb-3 label-icon-sort"
              isStyle={false}
              Icon={() => getOrderIcon(true)}
            />
          </OptionMenu>
          <OptionMenu onClick={() => setIsOrder(false)}>
            <LabelIcon
              label={t('general_descending')}
              color=""
              className="ml-1 body-text--primary action-option-order-label"
              classNameMain="mb-3 label-icon-sort"
              isStyle={false}
              Icon={() => getOrderIcon(false)}
            />
          </OptionMenu>
          <OptionMenu onClick={() => setIsOrder('none')}>
            <LabelIcon
              label={t('general_none')}
              color=""
              className="ml-1 body-text--primary action-option-order-label"
              classNameMain="mb-3 label-icon-sort"
              isStyle={false}
              Icon={() => getOrderIcon('none')}
            />
          </OptionMenu>
        </PopoverBody>
      </PopoverSort>
      <PopoverSort trigger="legacy" placement="bottom" target="PopoverFilterClient">
        <PopoverBody>
          <FilterOptionMenu aria-hidden="true" className="filter-field-container">
            {customFields.map((customField) => (
              <div className="mb-1 custom-field-container" key={customField.id}>
                <Checkbox
                  type="checkbox"
                  id="filter-customField"
                  label=""
                  checked={selectedCustomField.some((x) => x.id === customField.id)}
                  onClick={(e) => onSelectedCustomField(e, customField)}
                />
                <span className="filter-i18-name filter-span-client">
                  {getNameSelectedLanguage(customField.i18List, customField.type, customField.id)}
                </span>
              </div>
            ))}
          </FilterOptionMenu>
        </PopoverBody>
      </PopoverSort>
      <ContactsModal isOpen={openContactsImportModal} toggle={onCloseContactsImportModal} size="xl">
        <ModalHeader
          className="modal-header"
          backgroundColor={colors.transparent}
          onClose={onCloseContactsImportModal}
        />
        <ModalBody>
          <ContactsImportModal
            onCancel={onCloseContactsImportModal}
            onCloseContactsImportModal={onCloseContactsImportModal}
          />
        </ModalBody>
      </ContactsModal>
    </>
  );
};

export default HeaderActions;
